import { useQuery } from "@tanstack/react-query";
import {
  getSnowflakeJobGraphData,
  getDbtHealthChecks,
  getSnowflakeJobGraphSummary,
} from "@/helpers/apis";
import { Chart, ChartTypes } from "@uicore";
import { Stack } from "@altimateai/lego";
import { TotalCostHeader } from "@/pages/query/common/TotalCostHeader";
import { NativeSelect } from "@altimateai/lego";
import { createColorMapFromObjectKeys, OTHER } from "@/pages/query/common";
import dayjs from "dayjs";
import { formatCost } from "@/helpers/utils";
import { Bar, Rectangle, TooltipProps, Line } from "recharts";
import { CostChartTooltip } from "@/pages/query/Graph";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { SnowflakeJobFilters } from "@/pages/query/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useAppState } from "@/modules/app/useAppContext";
import { Spinner } from "@altimateai/lego";
import { useDbtChartData, computeSelectedLegends, COVERAGE_COLORS } from "./useDbtChartData";
import { useDbtGraphFilters } from "./useDbtGraphFilters";

interface DbtJobsGraphProps {
  filters?: SnowflakeJobFilters;
}

export enum ChartTypeEnum {
  COST = "cost",
  TEST_COVERAGE = "test_coverage",
}

export const DbtJobsGraph: FC<DbtJobsGraphProps> = () => {
  const { isInitialized } = useDatatableContext();
  const { currency } = useAppState();
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();

  // Chart state from URL params
  const chartType = (searchParams.get("chartType") || ChartTypeEnum.COST) as ChartTypeEnum;
  const aggregateBy = (searchParams.get("aggregateBy") || "day") as "day" | "month" | "year";

  const handleChartTypeChange = useCallback(
    (value: ChartTypeEnum) => {
      updateSearchParamsByKey({ chartType: value });
    },
    [updateSearchParamsByKey]
  );

  const handleAggregateByChange = useCallback(
    (value: "day" | "month" | "year") => {
      updateSearchParamsByKey({ aggregateBy: value });
    },
    [updateSearchParamsByKey]
  );

  const { startDate, endDate, graphParams, graphSummaryQueryKey, graphDataQueryKey } =
    useDbtGraphFilters(aggregateBy);

  const { data: graphData, isFetching: isGraphLoading } = useQuery({
    queryKey: graphDataQueryKey,
    queryFn: () => getSnowflakeJobGraphData(graphParams),
    enabled: chartType === ChartTypeEnum.COST && isInitialized,
  });

  const [legendLabelMap, setLegendLabelMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (graphData) {
      const { legends, labelMap: legendLabels } = computeSelectedLegends(graphData);
      setSelectedLegends(legends);
      setLegendLabelMap(legendLabels);
    }
  }, [graphData]);

  const { data: healthCheckData, isLoading: isHealthCheckLoading } = useQuery({
    queryKey: ["dbt_health_checks", startDate, endDate, chartType],
    queryFn: () =>
      getDbtHealthChecks({
        name: "Low test coverage in dbt models",
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        limit: 100,
      }),
    enabled: chartType === ChartTypeEnum.TEST_COVERAGE && isInitialized,
  });

  const { chartData, labelMap, projectNames } = useDbtChartData({
    chartType,
    graphData,
    healthCheckData,
  });

  // Merge label maps from both sources (hook and computeSelectedLegends)
  const combinedLabelMap = useMemo(
    () => ({ ...legendLabelMap, ...labelMap }),
    [legendLabelMap, labelMap]
  );

  const colorsMap = useMemo(() => {
    return createColorMapFromObjectKeys(graphData || [], {
      reservedMappings: { [OTHER]: "#5B9FFF" },
      excludeKeys: ["date"],
      nestedKeyExtractor: key => key,
    });
  }, [graphData]);

  return (
    <Stack direction="column" className="al-p-3 al-border al-rounded-md">
      <Stack align="center" justify={"between"}>
        <TotalCostHeader
          queryKey={graphSummaryQueryKey}
          queryFn={getSnowflakeJobGraphSummary}
          params={graphParams}
          title={"Total Jobs Cost"}
        />
        <Stack align={"center"} gap={"md"}>
          <NativeSelect
            label="Aggregate By:"
            showPlaceholder
            variant="outline"
            id="aggregate-by-selector"
            onChange={(value: unknown) =>
              handleAggregateByChange(value as "day" | "month" | "year")
            }
            value={aggregateBy}
            options={[
              { label: "Daily", value: "day" },
              { label: "Monthly", value: "month" },
              { label: "Yearly", value: "year" },
            ]}
          />
          <NativeSelect
            label="Chart of:"
            showPlaceholder
            variant="outline"
            id="chart-selector"
            onChange={(value: unknown) => handleChartTypeChange(value as ChartTypeEnum)}
            value={chartType}
            options={[
              { label: "Cost", value: ChartTypeEnum.COST },
              { label: "Test Coverage", value: ChartTypeEnum.TEST_COVERAGE },
            ]}
          />
        </Stack>
      </Stack>
      <div className="al-border al-rounded-md al-p-4 al-pb-10">
        {isGraphLoading || isHealthCheckLoading ? (
          <Stack className={"al-h-40"} justify={"center"} align={"center"}>
            <Spinner /> Loading graph...
          </Stack>
        ) : chartData.length > 0 ? (
          <Chart
            title={chartType === ChartTypeEnum.COST ? "Cost" : "Test Coverage"}
            xAxisDataKey="date"
            yAxisLabel={{ value: chartType === ChartTypeEnum.COST ? "Cost" : "Test Coverage (%)" }}
            xAxisLabelFormatter={value => {
              if (chartType !== ChartTypeEnum.COST) return value;
              if (aggregateBy === "month") return dayjs(value).format("MMM YYYY");
              if (aggregateBy === "year") return dayjs(value).format("YYYY");
              return dayjs(value).format("MMM-DD");
            }}
            yAxisLabelFormatter={value =>
              chartType === ChartTypeEnum.COST ? formatCost(value, currency) : `${value}%`
            }
            type={chartType === ChartTypeEnum.COST ? ChartTypes.BarChart : ChartTypes.LineChart}
            data={chartData}
            hideFullscreenButton
            tooltipProps={
              chartType === ChartTypeEnum.COST
                ? {
                    content: (props: TooltipProps<number, string>) => (
                      <CostChartTooltip {...props} />
                    ),
                  }
                : {}
            }
            chartProps={{}}
          >
            {({ barSize }) => {
              if (chartType === ChartTypeEnum.COST) {
                return (
                  <>
                    {selectedLegends?.map((d, i) => (
                      <Bar
                        key={d}
                        barSize={barSize}
                        dataKey={d}
                        name={combinedLabelMap[d] || d}
                        fill={colorsMap[d]}
                        stackId="single"
                        shape={(props: unknown) => {
                          (props as { radius: number[] }).radius =
                            i === selectedLegends.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0];
                          return <Rectangle {...(props as Record<string, unknown>)} />;
                        }}
                      />
                    ))}
                  </>
                );
              } else {
                return (
                  <>
                    {projectNames.map((projectName, index) => (
                      <Line
                        key={projectName}
                        type="monotone"
                        dataKey={projectName}
                        stroke={COVERAGE_COLORS[index % COVERAGE_COLORS.length]}
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name={projectName}
                      />
                    ))}
                  </>
                );
              }
            }}
          </Chart>
        ) : (
          <Stack className={"al-h-40"} justify={"center"} align={"center"}>
            No data is present
          </Stack>
        )}
      </div>
    </Stack>
  );
};
